import { Col, Row } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { IconInfoCircle } from '@tabler/icons-react';
import { isValidEmailAddress } from 'components/shared/emailValidation';
import {
  isFullNameValid,
  type OfferPortalWizardContext,
} from 'state_machines/OfferPortalWizardMachine';

import {
  CardHeading,
  FIELD_CARD,
  FIELD_INPUT,
  FieldError,
  fieldLabel,
  type Send,
} from './wizardFields';

/** Step 3 of 6 of the offer wizard — the buyer info the purchase contract needs. */
const DataForDocumentsStep = ({
  context,
  send,
}: {
  context: OfferPortalWizardContext;
  send: Send;
}) => {
  const nameViolation = context.fieldViolations['buyerName'];
  const emailViolation = context.fieldViolations['buyerEmail'];
  const nameInvalid =
    (context.showError && !isFullNameValid(context.buyerFullName)) || nameViolation != null;
  const emailInvalid =
    (context.showError && !isValidEmailAddress(context.buyerEmail.trim())) ||
    emailViolation != null;
  return (
    <>
      <Col className={FIELD_CARD}>
        <CardHeading
          title="Buyer’s info"
          caption="We’ll only use this to add your buyer to the purchase contract - nothing else."
        />
        <Col className="gap-2">
          <Text as="label" htmlFor="buyer-name" className={fieldLabel(nameInvalid)}>
            Buyer’s full name
          </Text>
          <Input
            id="buyer-name"
            type="text"
            value={context.buyerFullName}
            onChange={(event) => send({ type: 'SET_BUYER_FULL_NAME', value: event.target.value })}
            size="xs"
            className={FIELD_INPUT}
            aria-label="Buyer’s full name"
            aria-invalid={nameInvalid}
            error={nameInvalid}
            analyticsName="cosmos-make-offer-buyer-name"
          />
          {nameViolation != null ? <FieldError>{nameViolation}</FieldError> : null}
          {context.showError && !isFullNameValid(context.buyerFullName) ? (
            <FieldError>Enter the buyer’s full name.</FieldError>
          ) : null}
        </Col>
        <Col className="gap-2">
          <Text as="label" htmlFor="buyer-email" className={fieldLabel(emailInvalid)}>
            Buyer’s e-mail
          </Text>
          <Input
            id="buyer-email"
            type="email"
            value={context.buyerEmail}
            onChange={(event) => send({ type: 'SET_BUYER_EMAIL', value: event.target.value })}
            size="xs"
            className={FIELD_INPUT}
            aria-label="Buyer’s e-mail"
            aria-invalid={emailInvalid}
            error={emailInvalid}
            analyticsName="cosmos-make-offer-buyer-email"
          />
          {emailViolation != null ? <FieldError>{emailViolation}</FieldError> : null}
          {context.showError && !isValidEmailAddress(context.buyerEmail.trim()) ? (
            <FieldError>Check if this e-mail address is correct.</FieldError>
          ) : null}
        </Col>
      </Col>
      <Row className="items-start gap-3 rounded-[12px] bg-od-bg-accent-brand/10 p-4">
        <IconInfoCircle className="size-5 shrink-0 text-od-content-accent-brand" />
        <Text className="text-body-sm text-od-content-secondary">
          <Text as="span" className="font-medium text-od-content-primary">
            This info goes on the purchase contract.
          </Text>{' '}
          After your offer is reviewed, your listing partner prepares the contract from the info
          above and sends it to you and your buyer for signature.
        </Text>
      </Row>
    </>
  );
};

export default DataForDocumentsStep;
